'use client';

import React, { useState, useMemo } from 'react';
import { Plus, Search, Eye, CheckCircle, XCircle, Clock, X, FileText, TrendingUp, TrendingDown, AlertTriangle, Paperclip, Upload, Send, Check, Minus } from 'lucide-react';
import { FARMS, HOUSES_BY_FARM, MOCK_FLOCK_SUMMARIES } from '../../components/birdRecordsData';
import Icon from '@/components/ui/AppIcon';


// ─── Types ────────────────────────────────────────────────────────────────────

type AdjustmentType = 'positive' | 'negative';
type AdjustmentStatus = 'draft' | 'pending_approval' | 'approved' | 'rejected' | 'posted';

interface EvidenceFile {
  name: string;
  size: number;
  type: string;
}

interface AdjustmentRecord {
  id: string;
  adjustmentNumber: string;
  adjustmentDate: string;
  adjustmentType: AdjustmentType;
  farm: string;
  house: string;
  flock: string;
  flockNumber: string;
  birdType: string;
  breed: string;
  quantity: number;
  populationBefore: number;
  populationAfter: number;
  reason: string;
  reasonCategory: string;
  notes?: string;
  evidenceFiles: EvidenceFile[];
  reconciliationRef?: string;
  recordedBy: string;
  submittedBy?: string;
  reviewedBy?: string;
  approvedBy?: string;
  rejectionReason?: string;
  status: AdjustmentStatus;
  createdAt: string;
  updatedAt: string;
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const REASON_CATEGORIES: Record<AdjustmentType, string[]> = {
  positive: [
    'Physical count higher than system — recount confirmed',
    'Birds returned from quarantine not recorded',
    'Transfer-in not captured in system',
    'Hatch not recorded in system',
    'Purchase not entered in system',
    'System data entry error — undercounted',
    'Birds found in adjacent house — reassigned',
    'Other positive correction — see notes',
  ],
  negative: [
    'Unrecorded mortality confirmed during reconciliation',
    'Physical count lower than system — recount confirmed',
    'Transfer-out not captured in system',
    'Sale not recorded in system',
    'Culling not entered in system',
    'System data entry error — overcounted',
    'Birds missing — investigation complete',
    'Escaped birds — perimeter check done',
    'Other negative correction — see notes',
  ],
};

const APPROVERS = ['Farm Manager', 'Operations Director', 'General Manager', 'Dr. Sarah Namukasa'];
const RECORDERS = ['James Okello', 'Moses Wafula', 'Sarah Namukasa', 'Peter Ssemakula', 'Grace Atim'];

const MOCK_ADJUSTMENTS: AdjustmentRecord[] = [
  {
    id: 'adj-001',
    adjustmentNumber: 'ADJ-2026-000001',
    adjustmentDate: '2026-08-31',
    adjustmentType: 'negative',
    farm: 'Idudi Poultry Farm',
    house: 'House A1',
    flock: 'Batch A1 — Cobb 500',
    flockNumber: 'BR-2026-001',
    birdType: 'Broiler',
    breed: 'Cobb 500',
    quantity: 8,
    populationBefore: 4820,
    populationAfter: 4812,
    reason: 'Unrecorded mortality confirmed during reconciliation',
    reasonCategory: 'negative',
    notes: 'Birds found dead in corner during physical count REC-2026-000001. Mortality records updated separately.',
    evidenceFiles: [{ name: 'recount_photo_A1.jpg', size: 245000, type: 'image/jpeg' }, { name: 'reconciliation_report.pdf', size: 89000, type: 'application/pdf' }],
    reconciliationRef: 'REC-2026-000001',
    recordedBy: 'James Okello',
    submittedBy: 'James Okello',
    approvedBy: 'Farm Manager',
    status: 'posted',
    createdAt: '2026-08-31T16:00:00Z',
    updatedAt: '2026-08-31T18:30:00Z',
  },
  {
    id: 'adj-002',
    adjustmentNumber: 'ADJ-2026-000002',
    adjustmentDate: '2026-08-31',
    adjustmentType: 'negative',
    farm: 'Idudi Poultry Farm',
    house: 'Layer House 01',
    flock: 'Layers Batch L1 — Lohmann',
    flockNumber: 'LY-2026-001',
    birdType: 'Layer',
    breed: 'Lohmann Brown',
    quantity: 6,
    populationBefore: 7748,
    populationAfter: 7742,
    reason: 'Minor mortality not yet recorded in system',
    reasonCategory: 'negative',
    evidenceFiles: [{ name: 'count_sheet_L1.pdf', size: 120000, type: 'application/pdf' }],
    reconciliationRef: 'REC-2026-000002',
    recordedBy: 'Moses Wafula',
    submittedBy: 'Moses Wafula',
    reviewedBy: 'Sarah Namukasa',
    status: 'approved',
    createdAt: '2026-08-31T17:00:00Z',
    updatedAt: '2026-09-01T09:00:00Z',
  },
  {
    id: 'adj-003',
    adjustmentNumber: 'ADJ-2026-000003',
    adjustmentDate: '2026-09-04',
    adjustmentType: 'negative',
    farm: 'Idudi Poultry Farm',
    house: 'House A2',
    flock: 'Batch A2 — Ross 308',
    flockNumber: 'BR-2026-002',
    birdType: 'Broiler',
    breed: 'Ross 308',
    quantity: 6,
    populationBefore: 4620,
    populationAfter: 4614,
    reason: 'Physical count lower than system — recount confirmed',
    reasonCategory: 'negative',
    notes: 'Pending investigation into missing birds. Perimeter check scheduled.',
    evidenceFiles: [],
    reconciliationRef: 'REC-2026-000003',
    recordedBy: 'James Okello',
    status: 'pending_approval',
    createdAt: '2026-09-04T14:00:00Z',
    updatedAt: '2026-09-04T14:00:00Z',
  },
  {
    id: 'adj-004',
    adjustmentNumber: 'ADJ-2026-000004',
    adjustmentDate: '2026-09-05',
    adjustmentType: 'positive',
    farm: 'Kampala Demo Farm',
    house: 'House C1',
    flock: 'Batch C1 — Arbor Acres',
    flockNumber: 'BR-2026-004',
    birdType: 'Broiler',
    breed: 'Arbor Acres',
    quantity: 12,
    populationBefore: 5962,
    populationAfter: 5974,
    reason: 'Birds found in adjacent house — reassigned',
    reasonCategory: 'positive',
    notes: 'Batch C1 birds were mistakenly housed in House C2 during transfer. Corrected after physical verification.',
    evidenceFiles: [{ name: 'transfer_correction_note.pdf', size: 55000, type: 'application/pdf' }],
    recordedBy: 'Peter Ssemakula',
    submittedBy: 'Peter Ssemakula',
    status: 'draft',
    createdAt: '2026-09-05T09:00:00Z',
    updatedAt: '2026-09-05T09:00:00Z',
  },
  {
    id: 'adj-005',
    adjustmentNumber: 'ADJ-2026-000005',
    adjustmentDate: '2026-09-06',
    adjustmentType: 'negative',
    farm: 'Idudi Poultry Farm',
    house: 'Layer House 02',
    flock: 'Layers Batch L2 — Isa Brown',
    flockNumber: 'LY-2026-002',
    birdType: 'Layer',
    breed: 'ISA Brown',
    quantity: 7,
    populationBefore: 6325,
    populationAfter: 6318,
    reason: 'Unrecorded mortality confirmed during reconciliation',
    reasonCategory: 'negative',
    evidenceFiles: [],
    reconciliationRef: 'REC-2026-000005',
    recordedBy: 'Moses Wafula',
    status: 'draft',
    createdAt: '2026-09-06T08:00:00Z',
    updatedAt: '2026-09-06T08:00:00Z',
  },
];

// ─── Status Config ────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<AdjustmentStatus, { label: string; color: string; bg: string; icon: React.ElementType }> = {
  draft: { label: 'Draft', color: '#64748b', bg: '#f1f5f9', icon: FileText },
  pending_approval: { label: 'Pending Approval', color: '#d97706', bg: '#fef3c7', icon: Clock },
  approved: { label: 'Approved', color: '#2563eb', bg: '#dbeafe', icon: CheckCircle },
  rejected: { label: 'Rejected', color: '#dc2626', bg: '#fee2e2', icon: XCircle },
  posted: { label: 'Posted', color: '#16a34a', bg: '#dcfce7', icon: CheckCircle },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function StatusBadge({ status }: { status: AdjustmentStatus }) {
  const cfg = STATUS_CONFIG[status];
  const Icon = cfg.icon;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap"
      style={{ backgroundColor: cfg.bg, color: cfg.color }}
    >
      <Icon size={10} />
      {cfg.label}
    </span>
  );
}

function AdjTypeBadge({ type }: { type: AdjustmentType }) {
  if (type === 'positive') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
      <TrendingUp size={10} />+Positive
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">
      <TrendingDown size={10} />−Negative
    </span>
  );
}

// ─── Summary Cards ────────────────────────────────────────────────────────────

function SummaryCards({ records }: { records: AdjustmentRecord[] }) {
  const posted = records.filter(r => r.status === 'posted');
  const pending = records.filter(r => r.status === 'pending_approval');
  const approved = records.filter(r => r.status === 'approved');
  const totalPositive = posted.filter(r => r.adjustmentType === 'positive').reduce((s, r) => s + r.quantity, 0);
  const totalNegative = posted.filter(r => r.adjustmentType === 'negative').reduce((s, r) => s + r.quantity, 0);
  const netImpact = totalPositive - totalNegative;

  const cards = [
    { label: 'Pending Approval', value: pending.length.toString(), icon: Clock, color: '#d97706', bg: '#fef3c7' },
    { label: 'Approved (Unposted)', value: approved.length.toString(), icon: CheckCircle, color: '#2563eb', bg: '#dbeafe' },
    { label: 'Posted Adjustments', value: posted.length.toString(), icon: FileText, color: '#16a34a', bg: '#dcfce7' },
    { label: 'Net Population Impact', value: `${netImpact >= 0 ? '+' : ''}${netImpact.toLocaleString()}`, icon: netImpact >= 0 ? TrendingUp : TrendingDown, color: netImpact >= 0 ? '#16a34a' : '#dc2626', bg: netImpact >= 0 ? '#dcfce7' : '#fee2e2' },
    { label: 'Positive Corrections', value: `+${totalPositive.toLocaleString()}`, icon: TrendingUp, color: '#16a34a', bg: '#dcfce7' },
    { label: 'Negative Corrections', value: `-${totalNegative.toLocaleString()}`, icon: TrendingDown, color: '#dc2626', bg: '#fee2e2' },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-5">
      {cards.map(c => (
        <div key={c.label} className="bg-white rounded-xl border border-border/60 shadow-sm p-4 flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg flex-shrink-0" style={{ backgroundColor: c.bg }}>
            <c.icon className="h-4 w-4" style={{ color: c.color }} />
          </div>
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground font-medium truncate">{c.label}</p>
            <p className="text-xl font-semibold tracking-tight text-foreground">{c.value}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── New Adjustment Form ──────────────────────────────────────────────────────

interface NewAdjustmentFormProps {
  onClose: () => void;
  onSubmit: (r: Partial<AdjustmentRecord>) => void;
}

function NewAdjustmentForm({ onClose, onSubmit }: NewAdjustmentFormProps) {
  const [form, setForm] = useState({
    adjustmentType: 'negative' as AdjustmentType,
    farm: FARMS[0],
    house: '',
    flockNumber: '',
    quantity: '',
    reason: '',
    notes: '',
    reconciliationRef: '',
    recordedBy: RECORDERS[0],
  });
  const [evidenceFiles, setEvidenceFiles] = useState<EvidenceFile[]>([]);
  const [step, setStep] = useState<1 | 2>(1);

  const houses = HOUSES_BY_FARM[form.farm] || [];
  const flocks = MOCK_FLOCK_SUMMARIES.filter(f => f.farm === form.farm && (!form.house || f.house === form.house));
  const selectedFlock = MOCK_FLOCK_SUMMARIES.find(f => f.flockNumber === form.flockNumber);
  const systemPop = selectedFlock?.currentBirds ?? 0;
  const qty = Number(form.quantity) || 0;
  const populationAfter = form.adjustmentType === 'positive' ? systemPop + qty : systemPop - qty;
  const reasons = REASON_CATEGORIES[form.adjustmentType];

  const set = (k: string, v: string) => setForm(p => ({ ...p, [k]: v }));

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const mapped: EvidenceFile[] = files.map(f => ({ name: f.name, size: f.size, type: f.type }));
    setEvidenceFiles(prev => [...prev, ...mapped]);
    e.target.value = '';
  };

  const removeFile = (idx: number) => setEvidenceFiles(prev => prev.filter((_, i) => i !== idx));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFlock || !form.reason || qty <= 0) return;
    onSubmit({
      adjustmentType: form.adjustmentType,
      farm: form.farm,
      house: form.house,
      flock: selectedFlock.flockName,
      flockNumber: form.flockNumber,
      birdType: selectedFlock.birdType,
      breed: selectedFlock.breed,
      quantity: qty,
      populationBefore: systemPop,
      populationAfter,
      reason: form.reason,
      reasonCategory: form.adjustmentType,
      notes: form.notes || undefined,
      reconciliationRef: form.reconciliationRef || undefined,
      evidenceFiles,
      recordedBy: form.recordedBy,
      status: 'draft',
    });
    onClose();
  };

  const canProceed = form.farm && form.house && form.flockNumber && qty > 0 && form.reason;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">New Population Adjustment</h2>
            <p className="text-xs text-gray-500 mt-0.5">Record a system correction from reconciliation investigation</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
            <X size={16} className="text-gray-500" />
          </button>
        </div>

        {/* Step Indicator */}
        <div className="flex items-center gap-0 px-5 pt-4">
          {[{ n: 1, label: 'Adjustment Details' }, { n: 2, label: 'Evidence & Submit' }].map((s, i) => (
            <React.Fragment key={s.n}>
              <div className="flex items-center gap-2">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold transition-colors ${step >= s.n ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-400'}`}>
                  {step > s.n ? <Check size={12} /> : s.n}
                </div>
                <span className={`text-xs font-medium ${step >= s.n ? 'text-gray-900' : 'text-gray-400'}`}>{s.label}</span>
              </div>
              {i === 0 && <div className={`flex-1 h-px mx-3 ${step > 1 ? 'bg-blue-600' : 'bg-gray-200'}`} />}
            </React.Fragment>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-5">
          {step === 1 && (
            <>
              {/* Adjustment Type */}
              <div>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Adjustment Type</h3>
                <div className="grid grid-cols-2 gap-3">
                  {(['positive', 'negative'] as AdjustmentType[]).map(t => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => { set('adjustmentType', t); set('reason', ''); }}
                      className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left ${form.adjustmentType === t
                        ? t === 'positive' ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50' :'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${t === 'positive' ? 'bg-green-100' : 'bg-red-100'}`}>
                        {t === 'positive' ? <TrendingUp size={16} className="text-green-600" /> : <TrendingDown size={16} className="text-red-600" />}
                      </div>
                      <div>
                        <p className={`text-xs font-semibold ${t === 'positive' ? 'text-green-700' : 'text-red-700'}`}>
                          {t === 'positive' ? '+ Positive Adjustment' : '− Negative Adjustment'}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {t === 'positive' ? 'Increase system population' : 'Decrease system population'}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Location & Flock */}
              <div>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Location & Flock</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Farm *</label>
                    <select className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30" value={form.farm} onChange={e => { set('farm', e.target.value); set('house', ''); set('flockNumber', ''); }} required>
                      {FARMS.map(f => <option key={f}>{f}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">House *</label>
                    <select className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30" value={form.house} onChange={e => { set('house', e.target.value); set('flockNumber', ''); }} required>
                      <option value="">Select house</option>
                      {houses.map(h => <option key={h}>{h}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Flock *</label>
                    <select className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30" value={form.flockNumber} onChange={e => set('flockNumber', e.target.value)} required>
                      <option value="">Select flock</option>
                      {flocks.map(f => <option key={f.flockNumber} value={f.flockNumber}>{f.flockNumber} — {f.flockName}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* Population Preview */}
              {selectedFlock && (
                <div className={`rounded-xl border p-4 ${form.adjustmentType === 'positive' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">System Population</p>
                      <p className="text-lg font-bold text-gray-900">{systemPop.toLocaleString()}</p>
                    </div>
                    <div className="flex flex-col items-center justify-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${form.adjustmentType === 'positive' ? 'bg-green-200' : 'bg-red-200'}`}>
                        {form.adjustmentType === 'positive' ? <TrendingUp size={14} className="text-green-700" /> : <TrendingDown size={14} className="text-red-700" />}
                      </div>
                      <p className={`text-sm font-bold mt-1 ${form.adjustmentType === 'positive' ? 'text-green-700' : 'text-red-700'}`}>
                        {form.adjustmentType === 'positive' ? '+' : '−'}{qty || 0}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Population After</p>
                      <p className={`text-lg font-bold ${form.adjustmentType === 'positive' ? 'text-green-700' : 'text-red-700'}`}>{qty > 0 ? populationAfter.toLocaleString() : '—'}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Quantity & Reason */}
              <div>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Quantity & Reason</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Adjustment Quantity *</label>
                    <input
                      type="number"
                      min="1"
                      className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                      placeholder="Number of birds"
                      value={form.quantity}
                      onChange={e => set('quantity', e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Recorded By *</label>
                    <select className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30" value={form.recordedBy} onChange={e => set('recordedBy', e.target.value)} required>
                      {RECORDERS.map(r => <option key={r}>{r}</option>)}
                    </select>
                  </div>
                </div>
                <div className="mt-3">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Reason for Adjustment *</label>
                  <select className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30" value={form.reason} onChange={e => set('reason', e.target.value)} required>
                    <option value="">Select reason</option>
                    {reasons.map(r => <option key={r}>{r}</option>)}
                  </select>
                </div>
                <div className="mt-3">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Reconciliation Reference</label>
                  <input
                    type="text"
                    className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                    placeholder="e.g. REC-2026-000001"
                    value={form.reconciliationRef}
                    onChange={e => set('reconciliationRef', e.target.value)}
                  />
                </div>
                <div className="mt-3">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Additional Notes</label>
                  <textarea
                    rows={3}
                    className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30 resize-none"
                    placeholder="Describe the investigation findings and justification for this correction..."
                    value={form.notes}
                    onChange={e => set('notes', e.target.value)}
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  disabled={!canProceed}
                  onClick={() => setStep(2)}
                  className="px-4 py-2 text-xs font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Next: Evidence & Submit →
                </button>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              {/* Evidence Upload */}
              <div>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Supporting Evidence</h3>
                <p className="text-xs text-gray-500 mb-3">Attach photos, count sheets, investigation reports, or any documents supporting this adjustment.</p>

                <label className="flex flex-col items-center justify-center w-full h-28 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-colors">
                  <Upload size={20} className="text-gray-400 mb-2" />
                  <span className="text-xs font-medium text-gray-600">Click to upload files</span>
                  <span className="text-xs text-gray-400 mt-0.5">Images, PDFs, spreadsheets (max 10MB each)</span>
                  <input type="file" multiple accept="image/*,.pdf,.xlsx,.xls,.csv,.doc,.docx" className="hidden" onChange={handleFileChange} />
                </label>

                {evidenceFiles.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {evidenceFiles.map((f, i) => (
                      <div key={i} className="flex items-center gap-3 p-2.5 bg-gray-50 rounded-lg border border-gray-200">
                        <Paperclip size={14} className="text-gray-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-gray-800 truncate">{f.name}</p>
                          <p className="text-xs text-gray-400">{formatFileSize(f.size)}</p>
                        </div>
                        <button type="button" onClick={() => removeFile(i)} className="p-1 rounded hover:bg-gray-200 transition-colors">
                          <X size={12} className="text-gray-400" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {evidenceFiles.length === 0 && (
                  <div className="mt-2 flex items-center gap-2 p-2.5 bg-amber-50 rounded-lg border border-amber-200">
                    <AlertTriangle size={13} className="text-amber-600 flex-shrink-0" />
                    <p className="text-xs text-amber-700">No evidence attached. Approvers may request documentation before approving.</p>
                  </div>
                )}
              </div>

              {/* Summary */}
              <div className="bg-gray-50 rounded-xl border border-gray-200 p-4">
                <h3 className="text-xs font-semibold text-gray-700 mb-3">Adjustment Summary</h3>
                <div className="grid grid-cols-2 gap-y-2 gap-x-4 text-xs">
                  <div><span className="text-gray-500">Type:</span> <span className="font-medium ml-1">{form.adjustmentType === 'positive' ? '+ Positive' : '− Negative'}</span></div>
                  <div><span className="text-gray-500">Flock:</span> <span className="font-medium ml-1">{form.flockNumber}</span></div>
                  <div><span className="text-gray-500">Quantity:</span> <span className="font-medium ml-1">{qty.toLocaleString()} birds</span></div>
                  <div><span className="text-gray-500">Before:</span> <span className="font-medium ml-1">{systemPop.toLocaleString()}</span></div>
                  <div className="col-span-2"><span className="text-gray-500">Reason:</span> <span className="font-medium ml-1">{form.reason}</span></div>
                  <div><span className="text-gray-500">After:</span> <span className={`font-semibold ml-1 ${form.adjustmentType === 'positive' ? 'text-green-700' : 'text-red-700'}`}>{populationAfter.toLocaleString()}</span></div>
                  <div><span className="text-gray-500">Evidence:</span> <span className="font-medium ml-1">{evidenceFiles.length} file{evidenceFiles.length !== 1 ? 's' : ''}</span></div>
                </div>
              </div>

              <div className="flex items-center justify-between gap-3">
                <button type="button" onClick={() => setStep(1)} className="px-4 py-2 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                  ← Back
                </button>
                <div className="flex gap-2">
                  <button type="submit" className="px-4 py-2 text-xs font-semibold bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors">
                    Save as Draft
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (!selectedFlock || !form.reason || qty <= 0) return;
                      onSubmit({
                        adjustmentType: form.adjustmentType,
                        farm: form.farm,
                        house: form.house,
                        flock: selectedFlock.flockName,
                        flockNumber: form.flockNumber,
                        birdType: selectedFlock.birdType,
                        breed: selectedFlock.breed,
                        quantity: qty,
                        populationBefore: systemPop,
                        populationAfter,
                        reason: form.reason,
                        reasonCategory: form.adjustmentType,
                        notes: form.notes || undefined,
                        reconciliationRef: form.reconciliationRef || undefined,
                        evidenceFiles,
                        recordedBy: form.recordedBy,
                        submittedBy: form.recordedBy,
                        status: 'pending_approval',
                      });
                      onClose();
                    }}
                    className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Send size={12} />
                    Submit for Approval
                  </button>
                </div>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
}

// ─── Approval Modal ───────────────────────────────────────────────────────────

interface ApprovalModalProps {
  record: AdjustmentRecord;
  action: 'approve' | 'reject' | 'post';
  onClose: () => void;
  onConfirm: (id: string, action: 'approve' | 'reject' | 'post', approver?: string, rejectionReason?: string) => void;
}

function ApprovalModal({ record, action, onClose, onConfirm }: ApprovalModalProps) {
  const [approver, setApprover] = useState(APPROVERS[0]);
  const [rejectionReason, setRejectionReason] = useState('');

  const config = {
    approve: { title: 'Approve Adjustment', desc: 'Approve this population correction. It will be ready to post to the ledger.', btnLabel: 'Approve', btnClass: 'bg-blue-600 hover:bg-blue-700', icon: CheckCircle },
    reject: { title: 'Reject Adjustment', desc: 'Reject this adjustment and return it to the recorder with your reason.', btnLabel: 'Reject', btnClass: 'bg-red-600 hover:bg-red-700', icon: XCircle },
    post: { title: 'Post to Ledger', desc: 'Post this approved adjustment to the Bird Movement ledger. This will update the official population count.', btnLabel: 'Post Adjustment', btnClass: 'bg-green-600 hover:bg-green-700', icon: CheckCircle },
  }[action];

  const Icon = config.icon;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div className="flex items-center gap-2">
            <Icon size={16} className={action === 'reject' ? 'text-red-600' : action === 'post' ? 'text-green-600' : 'text-blue-600'} />
            <h2 className="text-sm font-semibold text-gray-900">{config.title}</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"><X size={16} className="text-gray-500" /></button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-xs text-gray-600">{config.desc}</p>

          {/* Record Summary */}
          <div className="bg-gray-50 rounded-lg border border-gray-200 p-3 space-y-1.5 text-xs">
            <div className="flex justify-between"><span className="text-gray-500">Adjustment</span><span className="font-medium">{record.adjustmentNumber}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Flock</span><span className="font-medium">{record.flockNumber}</span></div>
            <div className="flex justify-between">
              <span className="text-gray-500">Impact</span>
              <span className={`font-semibold ${record.adjustmentType === 'positive' ? 'text-green-700' : 'text-red-700'}`}>
                {record.adjustmentType === 'positive' ? '+' : '−'}{record.quantity.toLocaleString()} birds
              </span>
            </div>
            <div className="flex justify-between"><span className="text-gray-500">Population</span><span className="font-medium">{record.populationBefore.toLocaleString()} → {record.populationAfter.toLocaleString()}</span></div>
          </div>

          {action !== 'post' && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{action === 'approve' ? 'Approved By *' : 'Reviewed By *'}</label>
              <select className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30" value={approver} onChange={e => setApprover(e.target.value)}>
                {APPROVERS.map(a => <option key={a}>{a}</option>)}
              </select>
            </div>
          )}

          {action === 'reject' && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Rejection Reason *</label>
              <textarea
                rows={3}
                className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30 resize-none"
                placeholder="Explain why this adjustment is being rejected..."
                value={rejectionReason}
                onChange={e => setRejectionReason(e.target.value)}
                required
              />
            </div>
          )}

          <div className="flex gap-2 justify-end pt-1">
            <button type="button" onClick={onClose} className="px-4 py-2 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">Cancel</button>
            <button
              type="button"
              disabled={action === 'reject' && !rejectionReason.trim()}
              onClick={() => onConfirm(record.id, action, approver, rejectionReason || undefined)}
              className={`px-4 py-2 text-xs font-semibold text-white rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${config.btnClass}`}
            >
              {config.btnLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Detail Drawer ────────────────────────────────────────────────────────────

function DetailDrawer({ record, onClose, onAction }: { record: AdjustmentRecord; onClose: () => void; onAction: (action: 'approve' | 'reject' | 'post') => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-2xl bg-white rounded-2xl border border-border/60 shadow-xl overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">{record.adjustmentNumber}</h3>
            <p className="text-xs text-gray-500 mt-0.5">{record.adjustmentDate} · {record.farm}</p>
          </div>
          <div className="flex items-center gap-2">
            <AdjTypeBadge type={record.adjustmentType} />
            <StatusBadge status={record.status} />
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"><X size={14} /></button>
          </div>
        </div>

        <div className="p-5 overflow-y-auto max-h-[70vh] space-y-5">
          {/* Population Impact */}
          <div className={`rounded-xl border p-4 ${record.adjustmentType === 'positive' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            <p className="text-xs font-semibold text-gray-600 mb-3">Population Impact</p>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-xs text-gray-500">Before</p>
                <p className="text-xl font-bold text-gray-900">{record.populationBefore.toLocaleString()}</p>
              </div>
              <div className="flex flex-col items-center justify-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${record.adjustmentType === 'positive' ? 'bg-green-200' : 'bg-red-200'}`}>
                  {record.adjustmentType === 'positive' ? <TrendingUp size={14} className="text-green-700" /> : <TrendingDown size={14} className="text-red-700" />}
                </div>
                <p className={`text-sm font-bold mt-1 ${record.adjustmentType === 'positive' ? 'text-green-700' : 'text-red-700'}`}>
                  {record.adjustmentType === 'positive' ? '+' : '−'}{record.quantity.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">After</p>
                <p className={`text-xl font-bold ${record.adjustmentType === 'positive' ? 'text-green-700' : 'text-red-700'}`}>{record.populationAfter.toLocaleString()}</p>
              </div>
            </div>
          </div>

          {/* Details Grid */}
          <div className="grid grid-cols-2 gap-3 text-xs">
            {[
              { label: 'Flock', value: `${record.flockNumber} — ${record.flock}` },
              { label: 'House', value: record.house },
              { label: 'Bird Type', value: record.birdType },
              { label: 'Breed', value: record.breed },
              { label: 'Recorded By', value: record.recordedBy },
              { label: 'Reconciliation Ref', value: record.reconciliationRef || '—' },
            ].map(item => (
              <div key={item.label} className="bg-gray-50 rounded-lg p-3">
                <p className="text-gray-500 mb-0.5">{item.label}</p>
                <p className="font-medium text-gray-900">{item.value}</p>
              </div>
            ))}
          </div>

          {/* Reason */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Reason for Adjustment</p>
            <div className="bg-gray-50 rounded-lg border border-gray-200 p-3">
              <p className="text-xs text-gray-800">{record.reason}</p>
              {record.notes && <p className="text-xs text-gray-500 mt-2 border-t border-gray-200 pt-2">{record.notes}</p>}
            </div>
          </div>

          {/* Evidence */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Supporting Evidence ({record.evidenceFiles.length} file{record.evidenceFiles.length !== 1 ? 's' : ''})</p>
            {record.evidenceFiles.length > 0 ? (
              <div className="space-y-2">
                {record.evidenceFiles.map((f, i) => (
                  <div key={i} className="flex items-center gap-3 p-2.5 bg-gray-50 rounded-lg border border-gray-200">
                    <Paperclip size={13} className="text-gray-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-800 truncate">{f.name}</p>
                      <p className="text-xs text-gray-400">{formatFileSize(f.size)}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center gap-2 p-2.5 bg-amber-50 rounded-lg border border-amber-200">
                <AlertTriangle size={13} className="text-amber-600 flex-shrink-0" />
                <p className="text-xs text-amber-700">No evidence files attached to this adjustment.</p>
              </div>
            )}
          </div>

          {/* Approval Trail */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Approval Trail</p>
            <div className="space-y-2">
              {[
                { label: 'Recorded by', value: record.recordedBy, done: true },
                { label: 'Submitted by', value: record.submittedBy, done: !!record.submittedBy },
                { label: 'Reviewed by', value: record.reviewedBy, done: !!record.reviewedBy },
                { label: 'Approved by', value: record.approvedBy, done: !!record.approvedBy },
                { label: 'Posted', value: record.status === 'posted' ? 'Yes' : undefined, done: record.status === 'posted' },
              ].map(step => (
                <div key={step.label} className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${step.done ? 'bg-green-100' : 'bg-gray-100'}`}>
                    {step.done ? <Check size={10} className="text-green-600" /> : <Minus size={10} className="text-gray-400" />}
                  </div>
                  <span className="text-xs text-gray-500 w-24 flex-shrink-0">{step.label}</span>
                  <span className={`text-xs font-medium ${step.done ? 'text-gray-900' : 'text-gray-400'}`}>{step.value || 'Pending'}</span>
                </div>
              ))}
              {record.rejectionReason && (
                <div className="mt-2 p-2.5 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-xs font-medium text-red-700 mb-0.5">Rejection Reason</p>
                  <p className="text-xs text-red-600">{record.rejectionReason}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Action Footer */}
        {(record.status === 'pending_approval' || record.status === 'approved') && (
          <div className="flex items-center justify-end gap-2 p-4 border-t bg-gray-50">
            {record.status === 'pending_approval' && (
              <>
                <button onClick={() => onAction('reject')} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors">
                  <XCircle size={12} />Reject
                </button>
                <button onClick={() => onAction('approve')} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors">
                  <CheckCircle size={12} />Approve
                </button>
              </>
            )}
            {record.status === 'approved' && (
              <button onClick={() => onAction('post')} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors">
                <Send size={12} />Post to Ledger
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function BirdAdjustmentsContent() {
  const [records, setRecords] = useState<AdjustmentRecord[]>(MOCK_ADJUSTMENTS);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<AdjustmentStatus | 'all'>('all');
  const [filterType, setFilterType] = useState<AdjustmentType | 'all'>('all');
  const [showForm, setShowForm] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<AdjustmentRecord | null>(null);
  const [approvalAction, setApprovalAction] = useState<'approve' | 'reject' | 'post' | null>(null);

  const filtered = useMemo(() => {
    return records.filter(r => {
      const matchSearch = !search || [r.adjustmentNumber, r.flock, r.flockNumber, r.farm, r.house, r.reason].some(v => v?.toLowerCase().includes(search.toLowerCase()));
      const matchStatus = filterStatus === 'all' || r.status === filterStatus;
      const matchType = filterType === 'all' || r.adjustmentType === filterType;
      return matchSearch && matchStatus && matchType;
    });
  }, [records, search, filterStatus, filterType]);

  const handleCreate = (data: Partial<AdjustmentRecord>) => {
    const newRecord: AdjustmentRecord = {
      id: `adj-${Date.now()}`,
      adjustmentNumber: `ADJ-2026-${String(records.length + 1).padStart(6, '0')}`,
      adjustmentDate: new Date().toISOString().split('T')[0],
      adjustmentType: data.adjustmentType ?? 'negative',
      farm: data.farm ?? '',
      house: data.house ?? '',
      flock: data.flock ?? '',
      flockNumber: data.flockNumber ?? '',
      birdType: data.birdType ?? '',
      breed: data.breed ?? '',
      quantity: data.quantity ?? 0,
      populationBefore: data.populationBefore ?? 0,
      populationAfter: data.populationAfter ?? 0,
      reason: data.reason ?? '',
      reasonCategory: data.reasonCategory ?? '',
      notes: data.notes,
      evidenceFiles: data.evidenceFiles ?? [],
      reconciliationRef: data.reconciliationRef,
      recordedBy: data.recordedBy ?? '',
      submittedBy: data.submittedBy,
      status: data.status ?? 'draft',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    setRecords(prev => [newRecord, ...prev]);
  };

  const handleApprovalAction = (id: string, action: 'approve' | 'reject' | 'post', approver?: string, rejectionReason?: string) => {
    setRecords(prev => prev.map(r => {
      if (r.id !== id) return r;
      if (action === 'approve') return { ...r, status: 'approved', reviewedBy: approver, approvedBy: approver, updatedAt: new Date().toISOString() };
      if (action === 'reject') return { ...r, status: 'rejected', reviewedBy: approver, rejectionReason, updatedAt: new Date().toISOString() };
      if (action === 'post') return { ...r, status: 'posted', updatedAt: new Date().toISOString() };
      return r;
    }));
    setApprovalAction(null);
    setSelectedRecord(null);
  };

  const pendingCount = records.filter(r => r.status === 'pending_approval').length;

  return (
    <div className="p-4 md:p-6 space-y-5">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-bold text-foreground tracking-tight">Population Adjustments</h1>
          <p className="text-xs text-muted-foreground mt-0.5">Record and validate system population corrections from reconciliation investigations</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-4 py-2 text-xs font-semibold bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors shadow-sm"
        >
          <Plus size={14} />
          New Adjustment
        </button>
      </div>

      {/* Pending Alert */}
      {pendingCount > 0 && (
        <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl">
          <Clock size={15} className="text-amber-600 flex-shrink-0" />
          <p className="text-xs text-amber-800 font-medium">
            {pendingCount} adjustment{pendingCount > 1 ? 's' : ''} pending approval — review and approve to post population corrections to the ledger.
          </p>
        </div>
      )}

      {/* Summary Cards */}
      <SummaryCards records={records} />

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search adjustments, flocks, farms..."
            className="w-full pl-9 pr-4 py-2 text-xs border border-border/60 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div className="flex gap-2">
          <select
            className="text-xs border border-border/60 rounded-xl px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            value={filterType}
            onChange={e => setFilterType(e.target.value as AdjustmentType | 'all')}
          >
            <option value="all">All Types</option>
            <option value="positive">Positive</option>
            <option value="negative">Negative</option>
          </select>
          <select
            className="text-xs border border-border/60 rounded-xl px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value as AdjustmentStatus | 'all')}
          >
            <option value="all">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="pending_approval">Pending Approval</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="posted">Posted</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-border/60 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border/60 bg-gray-50/80">
                {['Adjustment #', 'Date', 'Type', 'Flock / House', 'Quantity', 'Before → After', 'Reason', 'Evidence', 'Status', ''].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border/40">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-12 text-center text-muted-foreground text-xs">
                    No adjustments found matching your filters.
                  </td>
                </tr>
              ) : (
                filtered.map(r => (
                  <tr key={r.id} className="hover:bg-gray-50/60 transition-colors">
                    <td className="px-4 py-3 font-mono font-medium text-gray-900 whitespace-nowrap">{r.adjustmentNumber}</td>
                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{r.adjustmentDate}</td>
                    <td className="px-4 py-3"><AdjTypeBadge type={r.adjustmentType} /></td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900 truncate max-w-[160px]">{r.flockNumber}</p>
                      <p className="text-gray-400 truncate max-w-[160px]">{r.house}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`font-bold text-sm ${r.adjustmentType === 'positive' ? 'text-green-700' : 'text-red-700'}`}>
                        {r.adjustmentType === 'positive' ? '+' : '−'}{r.quantity.toLocaleString()}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                      {r.populationBefore.toLocaleString()} → <span className={r.adjustmentType === 'positive' ? 'text-green-700 font-medium' : 'text-red-700 font-medium'}>{r.populationAfter.toLocaleString()}</span>
                    </td>
                    <td className="px-4 py-3 max-w-[200px]">
                      <p className="truncate text-gray-700">{r.reason}</p>
                      {r.reconciliationRef && <p className="text-gray-400 font-mono">{r.reconciliationRef}</p>}
                    </td>
                    <td className="px-4 py-3">
                      {r.evidenceFiles.length > 0 ? (
                        <span className="inline-flex items-center gap-1 text-blue-600">
                          <Paperclip size={11} />{r.evidenceFiles.length}
                        </span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setSelectedRecord(r)}
                          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors"
                          title="View details"
                        >
                          <Eye size={13} />
                        </button>
                        {r.status === 'pending_approval' && (
                          <>
                            <button
                              onClick={() => { setSelectedRecord(r); setApprovalAction('approve'); }}
                              className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-500 hover:text-blue-700 transition-colors"
                              title="Approve"
                            >
                              <CheckCircle size={13} />
                            </button>
                            <button
                              onClick={() => { setSelectedRecord(r); setApprovalAction('reject'); }}
                              className="p-1.5 rounded-lg hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors"
                              title="Reject"
                            >
                              <XCircle size={13} />
                            </button>
                          </>
                        )}
                        {r.status === 'approved' && (
                          <button
                            onClick={() => { setSelectedRecord(r); setApprovalAction('post'); }}
                            className="p-1.5 rounded-lg hover:bg-green-50 text-green-500 hover:text-green-700 transition-colors"
                            title="Post to ledger"
                          >
                            <Send size={13} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {filtered.length > 0 && (
          <div className="px-4 py-3 border-t border-border/40 bg-gray-50/50 flex items-center justify-between">
            <p className="text-xs text-muted-foreground">Showing {filtered.length} of {records.length} adjustments</p>
            <p className="text-xs text-muted-foreground">
              Net impact: <span className="font-semibold text-gray-900">
                {(() => {
                  const net = filtered.filter(r => r.status === 'posted').reduce((s, r) => r.adjustmentType === 'positive' ? s + r.quantity : s - r.quantity, 0);
                  return `${net >= 0 ? '+' : ''}${net.toLocaleString()} birds (posted)`;
                })()}
              </span>
            </p>
          </div>
        )}
      </div>

      {/* Modals */}
      {showForm && <NewAdjustmentForm onClose={() => setShowForm(false)} onSubmit={handleCreate} />}

      {selectedRecord && !approvalAction && (
        <DetailDrawer
          record={selectedRecord}
          onClose={() => setSelectedRecord(null)}
          onAction={(action) => setApprovalAction(action)}
        />
      )}

      {selectedRecord && approvalAction && (
        <ApprovalModal
          record={selectedRecord}
          action={approvalAction}
          onClose={() => { setApprovalAction(null); setSelectedRecord(null); }}
          onConfirm={handleApprovalAction}
        />
      )}
    </div>
  );
}
